import intlConfig from '../../intl.config';

const { languages, defaultLanguage, storageKey, legacyStorageKeys, legacyCleanupKeys, cookie } = intlConfig;

/**
 * Extracts a locale code (e.g. "en", "de-AT") from a pathname prefix, if present.
 *
 * @param {string} pathname - The pathname to inspect (e.g. "/en/blog/")
 * @returns {string|null} The lowercased locale code, or null for unprefixed paths
 */
export const getLocaleFromPath = (pathname) => {
  const match = (pathname || '').match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i);
  return match ? match[1].toLowerCase() : null;
};

/**
 * Maps a raw locale code (stored value, URL part, cookie or navigator language)
 * to a configured language. Region variants are reduced to their base code
 * ("en-US" → "en", "de-AT" → "de"). Unknown languages return null.
 *
 * @param {string|null} code - The raw locale code
 * @returns {string|null} A configured language code, or null
 */
export const normalizeLocale = (code) => {
  if (!code) return null;
  const lowercased = String(code).toLowerCase();
  if (languages.includes(lowercased)) return lowercased;
  const baseCode = lowercased.split('-')[0];
  return languages.includes(baseCode) ? baseCode : null;
};

/**
 * Reads ALL values of the shared language cookie. Older LP versions wrote the
 * cookie with `domain=.pinga.app` while the Nuxt app writes a host-only one —
 * browsers may hold both simultaneously.
 *
 * @returns {string[]} Raw cookie values in document order
 */
const readSharedCookieValues = () => {
  try {
    const prefix = `${cookie.key}=`;
    return document.cookie
      .split(';')
      .map((entry) => entry.trim())
      .filter((entry) => entry.startsWith(prefix))
      .map((entry) => decodeURIComponent(entry.slice(prefix.length)));
  } catch (e) {
    return [];
  }
};

/**
 * Reads the language cookie shared with the main Nuxt app.
 * While transitional duplicates exist (host-only + `.pinga.app` variant) and
 * they disagree, the LP's own last recorded choice (localStorage) is used as
 * a tie-breaker so a stale shadow cookie cannot override an explicit choice.
 *
 * @param {string|null} preferred - Normalized LP storage value for tie-breaking
 * @returns {string|null} The raw cookie value or null
 */
const readSharedCookie = (preferred) => {
  const values = [...new Set(readSharedCookieValues())];

  if (values.length === 0) return null;
  if (values.length > 1 && preferred) {
    const preferredRaw = values.find((value) => normalizeLocale(value) === preferred);
    if (preferredRaw) return preferredRaw;
  }
  return values[0];
};

/**
 * Persists the language preference: localStorage (LP's own key + legacy
 * cleanup) and the cookie shared with the Nuxt app.
 * The cookie is always written host-only. Before writing, both possible
 * variants (host-only and the legacy `domain=.pinga.app` one) are expired so
 * no stale duplicate can shadow the fresh value on the next read.
 *
 * @param {string} locale - A raw locale code; ignored when it maps to no
 *   configured language (e.g. "ru")
 */
export const persistLanguage = (locale) => {
  const value = normalizeLocale(locale);
  if (!value) return;

  try {
    window.localStorage.setItem(storageKey, value);
    legacyCleanupKeys.forEach((key) => window.localStorage.removeItem(key));
  } catch (e) {
    // localStorage may be unavailable (private mode) — cookie below still works
  }

  try {
    // Expire both variants: host-only and the legacy domain-scoped one.
    document.cookie = `${cookie.key}=; Max-Age=0; path=/; samesite=${cookie.sameSite}`;
    document.cookie = `${cookie.key}=; Max-Age=0; path=/; samesite=${cookie.sameSite}; domain=.pinga.app`;

    const maxAgeSeconds = cookie.maxAgeDays * 86400;
    document.cookie = `${cookie.key}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; path=/; samesite=${cookie.sameSite}`;
  } catch (e) {
    // cookies disabled — non-critical
  }
};

export const resolveLanguage = () => {
  let fromStorage = null;
  try {
    fromStorage = normalizeLocale(window.localStorage.getItem(storageKey));
  } catch (e) {
    // storage unavailable — cookie below still works
  }

  const fromCookie = normalizeLocale(readSharedCookie(fromStorage));
  if (fromCookie) return fromCookie;
  if (fromStorage) return fromStorage;

  try {
    const store = window.localStorage;
    for (const key of legacyStorageKeys) {
      const legacy = normalizeLocale(store.getItem(key));
      if (legacy) return legacy;
    }
  } catch (e) {
    // storage unavailable — fall through to browser detection
  }

  const browserLanguages = window.navigator.languages || [window.navigator.language];
  const detected = browserLanguages.map(normalizeLocale).find(Boolean);
  if (detected) return detected;

  return defaultLanguage;
};
